import React from 'react'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import { Typography } from '@mui/material'
import {
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  NoteV2,
} from '@labrav/react-components'
import { useDispatch, useSelector } from 'react-redux'

import { Role } from '../../utils/roles'
import { useUserType } from '../../utils/hooks/useUserType'
import { useGetUsers } from '../../utils/hooks/useGetUsers'
import type { UserProfileState } from '../../modules/userProfile/reducer'
import { AuthProvider } from '../../modules/userProfile/userProfileTypes'
import {
  addRowForAddUser,
  addUserErrorData,
  hideAddUserForm,
  removeRowForAddUser,
  submitAddNewUsersData,
  updateRowForAddUser,
} from '../../modules/addUserForm/actions'
import type {
  ErrorUsers,
  HandleType,
  UserType,
} from '../../modules/addUserForm/reducer'
import { extractAddUserRows } from '../../modules/addUserForm/utils'
import { HelpDrawer } from '../HelpDrawerV2'
import { useStyles } from './AddNewUserModal.styles'
import {
  buildAddNewMemberFields,
  getUserRoleOptions,
  hasAddUserErrors,
  STAFF_INVITE_DISABLED_MESSAGE,
} from './AddNewUserModal.utils'
import {
  applyAddUserRowChange,
  buildAddUserErrorState,
  clearAddUserRows,
  findAddUserRow,
  touchAddUserEmailError,
  type AddUserFormRow,
} from './AddNewUserModal.helpers'
import { AddUserRow } from './AddUserRow'
import { InvitedUsers } from './InvitedUsers/InvitedUsers'

type AddNewUserModalRootState = {
  userProfile: UserProfileState
  addNewUsers: {
    users: AddUserFormRow<UserType>[]
  }
  AccountData: {
    user?: {
      partnerData?: {
        partnerId?: string
      }
    }
  }
}

export const AddNewUserModal: React.FC<{
  openForm: boolean
  errors: ErrorUsers[]
}> = ({ openForm, errors }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const partnerType = useUserType()
  const { userProfile } = useSelector(
    (state: AddNewUserModalRootState) => state.userProfile
  )
  const isStaffUser =
    userProfile?.roles && userProfile?.roles[0] === Role.PARTNER_STAFF

  const formDisabled = !!isStaffUser

  const formDisabledToolTipMessage = STAFF_INVITE_DISABLED_MESSAGE

  const users = useSelector(
    (state: AddNewUserModalRootState) => state.addNewUsers.users
  )
  const { users: invitedUsers } = useGetUsers(partnerType)

  const partnerId = useSelector(
    (state: AddNewUserModalRootState) =>
      state.AccountData.user?.partnerData?.partnerId
  )

  const removeAllOtherRowsExceptFirst = async () => {
    await clearAddUserRows(dispatch, users)
  }
  const handleClose = () => {
    removeAllOtherRowsExceptFirst()
    dispatch(hideAddUserForm())
  }

  const validateForm = React.useCallback(
    (newUsers: AddUserFormRow<UserType>[]) => {
      dispatch(addUserErrorData(buildAddUserErrorState(newUsers, errors)))
    },
    [errors]
  )

  const handleInputChange = async (event: HandleType, idChanging: string) => {
    const updatedUser = findAddUserRow(users, idChanging)
    if (!updatedUser) {
      console.error(
        'updating user with id: ',
        idChanging,
        ' not possible because id was not found'
      )
      return
    }
    await dispatch(
      updateRowForAddUser(
        idChanging,
        applyAddUserRowChange(updatedUser, event)
      )
    )
  }

  const handleBlurInput = async (event: HandleType, idChanging: string) => {
    const updatedErr = touchAddUserEmailError(users, errors, event, idChanging)
    if (updatedErr) {
      await dispatch(addUserErrorData(updatedErr))
    }
  }

  const handleRemoveClick = async (id: string) => {
    await dispatch(removeRowForAddUser(id))
  }

  const handleAddClick = () => {
    dispatch(addRowForAddUser())
  }
  const userRoleOptions = getUserRoleOptions()

  const isDisable = hasAddUserErrors(errors)

  const onSubmit = async () => {
    if (partnerId) {
      await dispatch(
        submitAddNewUsersData(
          extractAddUserRows(users),
          partnerId,
          AuthProvider.AUTH0,
          false,
          undefined,
          false,
          partnerType
        ) as never
      )
      removeAllOtherRowsExceptFirst()
    }
  }

  React.useEffect(() => {
    validateForm(users)
  }, [users])
  const noOps = () => void 0
  void noOps

  const addNewMemberFields = buildAddNewMemberFields(
    formDisabled,
    userRoleOptions
  )

  return (
    <>
      <ModalConfirmV2
        title={'Manage team members'}
        description={
          <div className={classes.descriptionWrapper}>
            <Typography
              data-testid="team-member-decription"
              className={classes.description}
            >
              Invite your team members to come help you with the information
              required to onboard onto Labra.
            </Typography>
            <div
              data-testid="team-member-note-component"
              className={classes.note}
            >
              <NoteV2 description="The invitee will only get access to your Labra account by accepting the invite sent by Labra on their email." />
            </div>
          </div>
        }
        content={
          <AddUserRow
            users={users}
            handleInputChange={handleInputChange}
            errors={errors}
            addNewMemberFields={addNewMemberFields}
            handleRemoveClick={handleRemoveClick}
            handleAddClick={handleAddClick}
            handleBlurInput={handleBlurInput}
            isDisable={isDisable}
            formDisabled={formDisabled}
            formDisabledToolTipMessage={formDisabledToolTipMessage}
          />
        }
        hideCancelButton={true}
        buttonsPositionLeft={true}
        open={openForm}
        titleAcceptButton={'Invite'}
        onAccept={onSubmit}
        onDecline={handleClose}
        variant={ModalSizeVariants.large}
        icon={<PersonAddAltIcon />}
        secondaryActions={
          <HelpDrawer sectionName="inviteMembersHelp" headerTitle="Help" />
        }
        disabledAcceptButtonMessage={
          formDisabled ? formDisabledToolTipMessage : ''
        }
        acceptDisabled={formDisabled || isDisable}
        bottomContent={<InvitedUsers invitedUsers={invitedUsers} />}
      />
    </>
  )
}
